import { isEmpty } from 'ramda';
import { type FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { PATIENT_PROFILE_SCROLL_KEY_PREFIX } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useMedia,
  useScrollRestoration,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import {
  Button,
  Column,
  Layout,
  MobileLayout,
  WidgetCard,
  WidgetLayout,
} from '@/shared/ui';

import { usePurchasePrompt } from '@/entities/billing';
import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import { Footer } from '@/features/footer';

import { AgentChatHost } from '@/widgets/AgentChat';
import { DentalPhotoReportCard } from '@/widgets/DentalPhotoReportCard';
import { Header, MobileHeader } from '@/widgets/Header';
import { OrderReport } from '@/widgets/OrderReport';
import { PatientDocuments } from '@/widgets/PatientDocuments';
import { PatientInfo } from '@/widgets/PatientInfo';
import { PatientRecordingsCard } from '@/widgets/PatientRecordingsCard';
import { PurchasePrompt } from '@/widgets/PurchasePrompt';
import { RawMeshViewerCard } from '@/widgets/RawMeshViewerCard';
import { ReportCard } from '@/widgets/ReportCard';

import { DENTAL_PHOTO_WIDGET_ID, UnifiedItem } from './config';
import {
  useDentalPhotoWidgetScroll,
  usePatientProfileItems,
  usePatientProfileVisibility,
} from './hooks';
import { splitIntoColumns } from './lib';
import styles from './PatientProfile.module.scss';
import { PatientPersonalInfo } from './ui/PatientPersonalInfo/PatientPersonalInfo';
import { PatientProfileModals } from './ui/PatientProfileModals/PatientProfileModals';
import { ReportCardSkeletons } from './ui/ReportCardSkeletons/ReportCardSkeletons';

export const PatientProfile: FC = () => {
  const { patientID = '' } = useParams();
  const [agentDocumentsRefreshToken, setAgentDocumentsRefreshToken] =
    useState(0);
  const [showHidden, setShowHidden] = useState(false);

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const user = useAppSelector(userModel.selectors.selectCurrentUser);

  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );

  const {
    shouldShow: shouldShowPurchasePrompt,
    isOwner,
    openShop,
  } = usePurchasePrompt();

  const {
    CanEditPatient,
    CanViewStudies,
    dentalPhotoCount,
    enableAgentChatFeature,
    shouldShowPhotosWidget,
    showOrderReport,
  } = usePatientProfileVisibility(patientID);

  const { allItems, hiddenItems, showSkeletons, canViewArchive } =
    usePatientProfileItems(patientID);

  const [combinedLeftColumn, combinedRightColumn] = splitIntoColumns(allItems);
  const [hiddenLeftColumn, hiddenRightColumn] = splitIntoColumns(hiddenItems);

  const renderUnifiedItem = (item: UnifiedItem) =>
    item.type === 'report' ? (
      <ReportCard key={item.data.ID} structuredReportsAndStudies={item.data} />
    ) : (
      <RawMeshViewerCard key={item.data.ID} study={item.data} />
    );

  const showHiddenSection = canViewArchive && hiddenItems.length > 0;
  const isHiddenSectionExpanded = showHiddenSection && showHidden;

  const hiddenToggle = showHiddenSection && (
    <Button
      variant="tertiary"
      icon={showHidden ? 'eye' : 'eyeClosed'}
      onClick={() => setShowHidden((current) => !current)}
      className={styles.showHiddenButton}
    >
      {showHidden
        ? formatMessage({
            id: 'patientProfile.hideHiddenReports',
            defaultMessage: 'Collapse archived reports',
          })
        : formatMessage(
            {
              id: 'patientProfile.showHiddenReports',
              defaultMessage: 'Show archived reports ({count, number})',
            },
            { count: hiddenItems.length },
          )}
    </Button>
  );

  useScrollRestoration({
    storageKey: `${PATIENT_PROFILE_SCROLL_KEY_PREFIX}${patientID}`,
    isContentLoaded: !showSkeletons,
    maxAttempts: 10,
    attemptInterval: 200,
    timeoutMinutes: 5,
  });

  useEffect(() => {
    trackEvent({
      eventName: 'PatientProfile Page Viewed',
      properties: { patient_id: patientID },
      isPageViewEvent: true,
    });
  }, [patientID]);

  useDentalPhotoWidgetScroll({
    patientID,
    shouldShowPhotosWidget,
    dentalPhotoCount,
  });

  if (isEmpty(user) || patient === undefined) {
    return null;
  }

  const handleEditPatient = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.PatientModalForm,
        data: {
          patientID,
        },
      }),
    );
  };

  const openMobileOrderReportModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MobileOrderReportModal,
      }),
    );
  };

  return (
    <>
      {isMobile ? (
        <MobileLayout>
          <MobileHeader />

          <MobileLayout.Main>
            <PatientPersonalInfo
              canEditPatient={CanEditPatient}
              onEditPatient={handleEditPatient}
            />

            <PatientInfo patientID={patientID} />

            {enableAgentChatFeature && (
              <PatientDocuments
                key={patientID}
                patientID={patientID}
                canEdit={CanEditPatient}
                refreshToken={agentDocumentsRefreshToken}
              />
            )}

            {CanViewStudies && <PatientRecordingsCard patientID={patientID} />}

            {shouldShowPurchasePrompt && (
              <PurchasePrompt
                title={formatMessage({
                  id: 'orderReport.title',
                  defaultMessage: 'Create AI report',
                })}
                isOwner={isOwner}
                onOpenShop={openShop}
              />
            )}

            {CanViewStudies && showSkeletons && <ReportCardSkeletons />}

            {CanViewStudies && allItems.map(renderUnifiedItem)}

            {shouldShowPhotosWidget && (
              <DentalPhotoReportCard
                patientID={patientID}
                elementID={DENTAL_PHOTO_WIDGET_ID}
              />
            )}

            {hiddenToggle}

            {isHiddenSectionExpanded && hiddenItems.map(renderUnifiedItem)}
          </MobileLayout.Main>

          {showOrderReport && (
            <MobileLayout.Footer>
              <Button
                data-component="create-ai-report-button"
                className={styles.mobileOrderReportButton}
                onClick={openMobileOrderReportModal}
                icon="plus"
              >
                {formatMessage({
                  id: 'mobileOrderReportModal.createReport',
                  defaultMessage: 'Create AI report',
                })}
              </Button>
            </MobileLayout.Footer>
          )}
        </MobileLayout>
      ) : (
        <Layout>
          <Layout.Header>
            <Header />
          </Layout.Header>

          <Layout.Content className={styles.desktopContent}>
            <div className={styles.desktopWorkspace}>
              <Layout.Main className={styles.main}>
                <div className={styles.header} data-section="patient-profile">
                  <PatientPersonalInfo
                    canEditPatient={CanEditPatient}
                    onEditPatient={handleEditPatient}
                  />
                </div>

                <WidgetLayout>
                  <Column className={styles.column}>
                    <PatientInfo patientID={patientID} />

                    {enableAgentChatFeature && (
                      <PatientDocuments
                        key={patientID}
                        patientID={patientID}
                        canEdit={CanEditPatient}
                        refreshToken={agentDocumentsRefreshToken}
                      />
                    )}

                    {CanViewStudies && (
                      <PatientRecordingsCard patientID={patientID} />
                    )}

                    {CanViewStudies && showSkeletons && <ReportCardSkeletons />}

                    {CanViewStudies &&
                      combinedLeftColumn.map(renderUnifiedItem)}
                  </Column>

                  <Column className={styles.column}>
                    {shouldShowPurchasePrompt && (
                      <PurchasePrompt
                        title={formatMessage({
                          id: 'orderReport.title',
                          defaultMessage: 'Create AI report',
                        })}
                        isOwner={isOwner}
                        onOpenShop={openShop}
                      />
                    )}

                    {showOrderReport && (
                      <WidgetCard>
                        <OrderReport />
                      </WidgetCard>
                    )}

                    {CanViewStudies &&
                      combinedRightColumn.map(renderUnifiedItem)}

                    {shouldShowPhotosWidget && (
                      <DentalPhotoReportCard
                        patientID={patientID}
                        elementID={DENTAL_PHOTO_WIDGET_ID}
                      />
                    )}
                  </Column>
                </WidgetLayout>

                {showHiddenSection && (
                  <div className={styles.showHiddenSection}>
                    {hiddenToggle}

                    {isHiddenSectionExpanded && (
                      <WidgetLayout>
                        <Column className={styles.column}>
                          {hiddenLeftColumn.map(renderUnifiedItem)}
                        </Column>

                        <Column className={styles.column}>
                          {hiddenRightColumn.map(renderUnifiedItem)}
                        </Column>
                      </WidgetLayout>
                    )}
                  </div>
                )}
              </Layout.Main>
            </div>
          </Layout.Content>

          <Layout.Footer>
            <Footer />
          </Layout.Footer>
        </Layout>
      )}

      {enableAgentChatFeature && (
        <AgentChatHost
          patientID={patientID}
          onDocumentChanged={() =>
            setAgentDocumentsRefreshToken((current) => current + 1)
          }
        />
      )}

      <PatientProfileModals patientID={patientID} />
    </>
  );
};
